/**
 * Page setup editing (size/orientation/margins). One of the three editors the plan described as
 * "tabbed side panels beneath for Page setup / Header / Footer / Variables"; all three shipped as
 * toolbar popovers instead, to stay consistent with the existing Link/Image button pattern rather
 * than force a page-layout redesign. See HeaderFooterBtn.tsx and FootnoteEndnoteBtn.tsx for the
 * other two.
 *
 * Operates on any section, not just the first. It used to READ section 0's `page` while WRITING
 * through `updateAttributes('section', …)`, which targets whichever section the cursor is in — so in
 * a multi-section document the popover showed section 1's size and applied the edit somewhere else,
 * and section 2+ could not be edited on purpose at all. Both ends now address one explicit section
 * index: the one holding the cursor by default, or whichever the picker selects.
 *
 * The write goes through a transaction against that section's own position rather than through
 * `updateAttributes`, precisely because `updateAttributes` reads the selection — which is the thing
 * the picker has to be able to override without dragging the user's cursor around the document.
 *
 * TemplateEditorPage.tsx's canvas still sizes itself from section 0 (one canvas, one width); that is
 * a separate limitation and unchanged here.
 */
import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRulerCombined } from '@fortawesome/free-solid-svg-icons'
import type { useEditor } from '@tiptap/react'
import { useEditorState } from '@tiptap/react'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import { Select } from '../ui'
import { PopoverBtn } from './ToolbarControls'
import { PageSize, Orientation, defaultPageSetup, type PageSetup, type Margins } from '../../cdm/types'
import { PAGE_PRESETS, CUSTOM_PRESET_ID, matchPagePreset, applyPagePreset } from './pagePresets'

type CdmEditor = ReturnType<typeof useEditor>

// Flat rather than grouped: the shared Select is react-select typed as a flat SelectOption[], and
// the slide entries carry a "Slide · " prefix, which reads as a group without changing a component
// every other page depends on.
const SIZE_OPTIONS = [
  ...PAGE_PRESETS.map(p => ({ value: p.id, label: p.label })),
  { value: CUSTOM_PRESET_ID, label: 'Custom…' },
]

const MARGIN_FIELDS: Array<{ key: keyof Margins; label: string }> = [
  { key: 'top', label: 'Top' },
  { key: 'right', label: 'Right' },
  { key: 'bottom', label: 'Bottom' },
  { key: 'left', label: 'Left' },
  { key: 'header', label: 'Header' },
  { key: 'footer', label: 'Footer' },
]

export default function PageSetupBtn({ editor }: { editor: CdmEditor }) {
  const [open, setOpen] = useState(false)
  // The dropdown's value is DERIVED from the dimensions, which is what lets an imported document
  // show "Slide · 16:9". But that alone cannot express "I want to type my own numbers" while the
  // current numbers still happen to equal a preset — choosing Custom at 612 × 792 would derive
  // back to Letter and leave the fields locked. This remembers the explicit choice.
  const [customChosen, setCustomChosen] = useState(false)
  // null = follow the cursor. Set only when the user picks a section explicitly, and cleared each
  // time the popover opens so it goes back to "the section I am working in".
  const [pickedSection, setPickedSection] = useState<number | null>(null)

  const doc = useEditorState({
    editor,
    selector: e => {
      const state = e.editor?.state
      if (!state) return { pages: [] as PageSetup[], cursorIndex: 0 }
      const pages: PageSetup[] = []
      state.doc.forEach(node => {
        pages.push((node.attrs?.page as PageSetup | undefined) ?? defaultPageSetup())
      })
      // index(0) is the position's top-level child — the section it is inside. A selection resolved
      // at the very top of the document has no depth-1 ancestor, hence the guard.
      const $from = state.selection.$from
      return { pages, cursorIndex: $from.depth > 0 ? $from.index(0) : 0 }
    },
  }) ?? { pages: [] as PageSetup[], cursorIndex: 0 }

  const sectionCount = doc.pages.length
  // Clamped because a section can be deleted while the popover is open, which would otherwise leave
  // the picker pointing past the end of the document.
  const sectionIndex = Math.min(pickedSection ?? doc.cursorIndex, Math.max(0, sectionCount - 1))
  const page = doc.pages[sectionIndex] ?? defaultPageSetup()

  useEffect(() => {
    if (open) setPickedSection(null)
  }, [open])

  function update(patch: Partial<PageSetup>) {
    const next = { ...page, ...patch }
    // Addressed by position rather than by selection: updateAttributes would write to whichever
    // section the cursor happens to be in, which is the bug this replaces. `focus` is deliberately
    // not chained either — editing section 3's margins must not move the caret into section 3.
    editor?.commands.command(({ tr, state, dispatch }) => {
      let pos = 0
      for (let i = 0; i < sectionIndex && i < state.doc.childCount; i++) pos += state.doc.child(i).nodeSize
      if (sectionIndex >= state.doc.childCount) return false
      if (dispatch) dispatch(tr.setNodeAttribute(pos, 'page', next))
      return true
    })
  }

  // The selection is derived from the dimensions rather than stored, so a document that arrives
  // with 960 × 540 shows "Slide · 16:9" even though the CDM only records it as Custom.
  const matchedPreset = matchPagePreset(page)
  const activePreset = customChosen ? null : matchedPreset
  const selectedId = activePreset?.id ?? CUSTOM_PRESET_ID

  function setPreset(id: string) {
    // Choosing "Custom…" changes nothing about the page; it just unlocks the width and height
    // boxes. Rewriting the dimensions here would throw away the size the user is about to edit.
    if (id === CUSTOM_PRESET_ID) {
      setCustomChosen(true)
      update({ size: PageSize.Custom })
      return
    }

    const preset = PAGE_PRESETS.find(p => p.id === id)
    if (!preset) return
    setCustomChosen(false)
    update(applyPagePreset(preset, page.orientation))
  }

  function setOrientation(orientation: Orientation) {
    if (orientation === page.orientation) return
    update({ orientation, widthPt: page.heightPt, heightPt: page.widthPt })
  }

  function setMargin(key: keyof Margins, value: number) {
    update({ margins: { ...page.margins, [key]: value } })
  }

  // Editable whenever the current dimensions match no preset — which covers both "the user picked
  // Custom" and "this document arrived with a size we do not name".
  const isCustom = activePreset === null

  return (
    <PopoverBtn icon={faRulerCombined} tooltipText="Page setup" active={open} onActivate={() => setOpen(true)} onDeactivate={() => setOpen(false)}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm, minWidth: 280 }}>
        {/* Only shown when there is a choice to make: a single-section document, which is almost
            every document, sees exactly the popover it saw before. */}
        {sectionCount > 1 && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: fontSize.sm, color: colors.textSecondary }}>
            Section
            <Select
              inputSize="sm"
              value={String(sectionIndex)}
              onChange={v => setPickedSection(Number(v))}
              options={doc.pages.map((_, i) => ({ value: String(i), label: `Section ${i + 1}` }))}
            />
          </label>
        )}

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: fontSize.sm, color: colors.textSecondary }}>
          Page size
          <Select
            inputSize="sm"
            value={selectedId}
            onChange={v => setPreset(String(v))}
            options={SIZE_OPTIONS}
          />
        </label>

        <div style={{ display: 'flex', gap: spacing.sm }}>
          <button
            type="button"
            onClick={() => setOrientation(Orientation.Portrait)}
            style={{ flex: 1, padding: '6px 10px', border: `1px solid ${page.orientation === Orientation.Portrait ? colors.primary : colors.border}`, borderRadius: radius.sm, background: page.orientation === Orientation.Portrait ? colors.primaryLight : 'none', color: page.orientation === Orientation.Portrait ? colors.primary : colors.text, fontSize: fontSize.sm, fontWeight: page.orientation === Orientation.Portrait ? 600 : 400, cursor: 'pointer' }}
          >
            Portrait
          </button>
          <button
            type="button"
            onClick={() => setOrientation(Orientation.Landscape)}
            style={{ flex: 1, padding: '6px 10px', border: `1px solid ${page.orientation === Orientation.Landscape ? colors.primary : colors.border}`, borderRadius: radius.sm, background: page.orientation === Orientation.Landscape ? colors.primaryLight : 'none', color: page.orientation === Orientation.Landscape ? colors.primary : colors.text, fontSize: fontSize.sm, fontWeight: page.orientation === Orientation.Landscape ? 600 : 400, cursor: 'pointer' }}
          >
            Landscape
          </button>
        </div>

        <div style={{ display: 'flex', gap: spacing.sm }}>
          <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, fontSize: fontSize.sm, color: colors.textSecondary }}>
            Width (pt)
            <input
              type="number"
              value={page.widthPt}
              disabled={!isCustom}
              onChange={e => update({ widthPt: Number(e.target.value) || 0 })}
              style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, outline: 'none', width: '100%', boxSizing: 'border-box', background: isCustom ? colors.surface : colors.background, color: isCustom ? colors.text : colors.textMuted }}
            />
          </label>
          <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, fontSize: fontSize.sm, color: colors.textSecondary }}>
            Height (pt)
            <input
              type="number"
              value={page.heightPt}
              disabled={!isCustom}
              onChange={e => update({ heightPt: Number(e.target.value) || 0 })}
              style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, outline: 'none', width: '100%', boxSizing: 'border-box', background: isCustom ? colors.surface : colors.background, color: isCustom ? colors.text : colors.textMuted }}
            />
          </label>
        </div>

        <div style={{ fontSize: fontSize.xs, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Margins (pt)</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: spacing.sm }}>
          {MARGIN_FIELDS.map(({ key, label }) => (
            <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: fontSize.sm, color: colors.textSecondary }}>
              {label}
              <input
                type="number"
                value={page.margins[key]}
                onChange={e => setMargin(key, Number(e.target.value) || 0)}
                style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, outline: 'none', width: '100%', boxSizing: 'border-box' }}
              />
            </label>
          ))}
        </div>
      </div>
    </PopoverBtn>
  )
}
